import React, { useEffect, useRef } from 'react';
import { Marker, Popup } from 'react-leaflet';
import { TemporaryLocation, LocationType } from '../../../types';
import { getMyTempLocationIcon } from '../utils';

interface LocationTypeInfo {
  value: LocationType;
  label: string;
  icon: string;
  color: string;
}

interface MyTempLocationMarkerProps {
  tempLocation: TemporaryLocation;
  locationType: LocationTypeInfo;
  locationTypes: LocationTypeInfo[];
  onEdit: () => void;
  onDirections: (info: {
    lat: number;
    lng: number;
    name?: string;
    address?: string;
    profilePhoto?: string;
    firstName?: string;
    lastName?: string;
  }) => void;
  onToggleActive: (id: number, currentStatus: boolean) => Promise<void>;
  // User info for popup display
  userName: string;
  userPhoto?: string | null;
  getPhotoUrl: (photo?: string) => string | null;
  // Auto-open popup if highlighted
  shouldOpenPopup?: boolean;
}

export const MyTempLocationMarker: React.FC<MyTempLocationMarkerProps> = ({
  tempLocation,
  locationType,
  locationTypes,
  onEdit,
  onDirections,
  onToggleActive,
  userName,
  userPhoto,
  getPhotoUrl,
  shouldOpenPopup = false,
}) => {
  const markerRef = useRef<any>(null);
  const icon = getMyTempLocationIcon(tempLocation.type, locationTypes);

  useEffect(() => {
    if (shouldOpenPopup && markerRef.current) {
      markerRef.current.openPopup();
    }
  }, [shouldOpenPopup]);

  const getTimeStatus = (startDate: Date, endDate: Date): { label: string; color: string } => {
    const now = new Date();

    if (now < startDate) {
      return { label: 'À venir', color: '#3498db' }; // Bleu
    } else if (now > endDate) {
      return { label: 'Passé', color: '#95a5a6' }; // Gris
    } else {
      return { label: 'En cours', color: '#28a745' }; // Vert
    }
  };

  const startDate = new Date(tempLocation.start_datetime);
  const endDate = new Date(tempLocation.end_datetime);
  const timeStatus = getTimeStatus(startDate, endDate);

  const photoUrl = userPhoto ? getPhotoUrl(userPhoto) : null;

  return (
    <Marker
      ref={markerRef}
      key={`my-temp-${tempLocation.id}`}
      position={[tempLocation.latitude, tempLocation.longitude]}
      icon={icon}
      opacity={tempLocation.is_active ? 1 : 0.5}
    >
      <Popup>
        <div className="map-popup my-temp-location-popup">
          {/* Photo in circle */}
          {photoUrl && (
            <img
              src={photoUrl}
              alt={userName}
              className="popup-photo"
            />
          )}

          {/* Name */}
          <h3>{userName}</h3>

          {/* "Adresse Temporaire - " + time status */}
          <p className="temp-location-subtitle">
            Adresse Temporaire - <span style={{ color: timeStatus.color, fontWeight: 600 }}>{timeStatus.label}</span>
          </p>

          {/* Location name with emoji */}
          <p className="temp-location-name">
            {locationType.value !== 'other' && locationType.icon} {tempLocation.name}
          </p>

          {/* Description */}
          {tempLocation.description && (
            <p className="temp-location-description">{tempLocation.description}</p>
          )}

          {/* Type badge - only if not "other" */}
          {locationType.value !== 'other' && (
            <span className="temp-location-type-badge" style={{ backgroundColor: locationType.color }}>
              {locationType.label}
            </span>
          )}

          {/* Dates - smart formatting based on location type */}
          <p className="temp-location-dates">
            {(() => {
              const sameDay = startDate.toDateString() === endDate.toDateString();
              const type = tempLocation.type;

              const formatDate = (d: Date) => d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
              const formatTime = (d: Date) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }).replace(':', 'h');

              if (type === 'party') {
                // Soirée: just the date, with hours if same day
                const dayStr = formatDate(startDate);
                if (sameDay) {
                  return <>{dayStr}<br />de {formatTime(startDate)} à {formatTime(endDate)}</>;
                }
                return `Le ${dayStr}`;
              } else if (type === 'work') {
                // Travail: period
                return <>Du {formatDate(startDate)}<br />au {formatDate(endDate)}</>;
              } else if (type === 'vacation') {
                // Vacances: until date
                return `Jusqu'au ${formatDate(endDate)}`;
              } else {
                // Other: smart format based on same day or not
                if (sameDay) {
                  return <>{formatDate(startDate)}<br />de {formatTime(startDate)} à {formatTime(endDate)}</>;
                } else {
                  return <>Du {formatDate(startDate)}<br />au {formatDate(endDate)}</>;
                }
              }
            })()}
          </p>

          {/* Bottom actions: Itinéraire - Modifier - Toggle on same row */}
          <div className="my-temp-popup-actions">
            <button
              className="my-temp-action-btn directions-btn"
              onClick={() => onDirections({
                lat: tempLocation.latitude,
                lng: tempLocation.longitude,
                name: tempLocation.name,
                address: tempLocation.address,
                profilePhoto: userPhoto || undefined,
              })}
              title="Itinéraire"
            >
              <img
                src="/itinéraire-icon-blanc.png"
                alt="Itinéraire"
                style={{ width: '16px', height: '16px' }}
              />
            </button>

            <button
              className="my-temp-action-btn edit-btn"
              onClick={onEdit}
              title="Modifier"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }}>
                <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>

            <button
              className={`popup-toggle-button ${tempLocation.is_active ? 'active' : 'inactive'}`}
              onClick={async (e) => {
                e.stopPropagation();
                await onToggleActive(tempLocation.id, tempLocation.is_active);
              }}
              title={tempLocation.is_active ? 'Désactiver' : 'Activer'}
            >
              <div className="toggle-slider-small"></div>
            </button>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};
